import { useState } from 'react';
import { Box, Typography, FormHelperText, useTheme, alpha } from '@mui/material';
import MDEditor from '@uiw/react-md-editor';
import rehypeSanitize from 'rehype-sanitize';

interface Props {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  helperText?: string;
  required?: boolean;
  height?: number;
  placeholder?: string;
}

export default function MarkdownEditor({
  label,
  value,
  onChange,
  helperText,
  required,
  height = 320,
  placeholder,
}: Props) {
  const theme = useTheme();
  const [focused, setFocused] = useState(false);

  const borderColor = focused
    ? theme.palette.primary.main
    : alpha(theme.palette.text.primary, 0.23);

  return (
    <Box sx={{ width: '100%' }}>
      {label && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mb: 0.75,
            color: focused ? 'primary.main' : 'text.secondary',
            fontSize: 12,
            fontWeight: 500,
            letterSpacing: 0.2,
            transition: 'color 0.2s',
          }}
        >
          {label}
          {required ? ' *' : ''}
        </Typography>
      )}

      <Box
        data-color-mode={theme.palette.mode}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          border: `1px solid ${borderColor}`,
          boxShadow: focused ? `0 0 0 3px ${alpha(theme.palette.primary.main, 0.12)}` : 'none',
          transition: 'border-color 0.2s, box-shadow 0.2s',
          backgroundColor: theme.palette.background.paper,

          '& .w-md-editor': {
            backgroundColor: 'transparent',
            boxShadow: 'none',
            border: 'none',
            color: theme.palette.text.primary,
            fontFamily: theme.typography.fontFamily,
            '--md-editor-border-color': 'transparent',
          },
          '& .w-md-editor-toolbar': {
            backgroundColor: alpha(theme.palette.text.primary, 0.03),
            borderBottom: `1px solid ${alpha(theme.palette.divider, 1)}`,
            padding: '6px 8px',
            '& ul > li > button': {
              borderRadius: 6,
              color: theme.palette.text.secondary,
              transition: 'background-color 0.15s, color 0.15s',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                color: theme.palette.primary.main,
              },
            },
            '& ul > li.active > button': {
              backgroundColor: alpha(theme.palette.primary.main, 0.12),
              color: theme.palette.primary.main,
            },
            '& .w-md-editor-toolbar-divider': {
              backgroundColor: theme.palette.divider,
            },
          },
          '& .w-md-editor-content': {
            backgroundColor: 'transparent',
          },
          '& .w-md-editor-text': {
            padding: '12px 14px',
          },
          '& .w-md-editor-text-pre, & .w-md-editor-text-input, & .w-md-editor-text > .w-md-editor-text-pre > code':
            {
              fontSize: 14,
              lineHeight: 1.6,
              fontFamily: '"JetBrains Mono", Menlo, Consolas, monospace',
              color: `${theme.palette.text.primary} !important`,
            },
          '& .w-md-editor-preview': {
            padding: '12px 16px',
            backgroundColor: alpha(theme.palette.text.primary, 0.015),
            borderLeft: `1px solid ${theme.palette.divider}`,
            boxShadow: 'none',
          },
          '& .wmde-markdown': {
            backgroundColor: 'transparent',
            color: theme.palette.text.primary,
            fontSize: 14,
            lineHeight: 1.6,
            '& h1, & h2, & h3, & h4': {
              borderBottom: 'none',
              marginTop: 8,
              marginBottom: 8,
            },
            '& code': {
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
              color: theme.palette.primary.main,
              padding: '2px 6px',
              borderRadius: 4,
              fontSize: '0.9em',
            },
            '& pre': {
              backgroundColor: alpha(theme.palette.text.primary, 0.04),
              borderRadius: 6,
              padding: 12,
            },
            '& pre code': {
              backgroundColor: 'transparent',
              color: theme.palette.text.primary,
              padding: 0,
            },
            '& table': {
              borderCollapse: 'collapse',
              border: `1px solid ${theme.palette.divider}`,
            },
            '& th, & td': {
              border: `1px solid ${theme.palette.divider}`,
              padding: '6px 10px',
            },
            '& th': {
              backgroundColor: alpha(theme.palette.text.primary, 0.04),
            },
            '& blockquote': {
              borderLeft: `3px solid ${theme.palette.primary.main}`,
              backgroundColor: alpha(theme.palette.primary.main, 0.04),
              color: theme.palette.text.secondary,
              margin: 0,
              padding: '4px 12px',
            },
          },
        }}
      >
        <MDEditor
          value={value}
          onChange={(v) => onChange(v ?? '')}
          height={height}
          preview="live"
          visibleDragbar={false}
          textareaProps={{ placeholder }}
          previewOptions={{ rehypePlugins: [[rehypeSanitize]] }}
        />
      </Box>

      {helperText && <FormHelperText sx={{ mt: 0.75, ml: 0.5 }}>{helperText}</FormHelperText>}
    </Box>
  );
}
